import _ from 'lodash'
import { ENTITY_TYPE } from '../../components/model/nlu-model/sidebar/entities/entity-types'
import nluApi from '../../api/nlu'

export default {
  namespaced: true,

  state: {
    listEntities: {
      loading: false,
      value: []
    },
    machineLearningEntities: {
      loading: true,
      value: []
    },
    regexpEntities: { loading: true, value: [] },
    patternAnyEntities: { loading: true, value: [] },
    prebuiltEntities: { loading: true, value: [] },
    allPrebuiltEntities: { loading: false, value: [] },
    postprocessEnities: { loading: false, value: [] },
    allEntities: { loading: false, value: [] }
  },

  getters: {
    allSynonyms (state) {
      return _.flatMapDeep(state.listEntities.value, item => _.map(item.subLists, 'list'))
    }
  },

  actions: {
    async fetchAllEntities ({ state, commit, dispatch }) {
      try {
        commit('SET_ALL_ENTITIES', { loading: true })

        await Promise.all([
          dispatch('fetchRegexpEntities'),
          dispatch('fetchListEntities'),
          dispatch('fetchMachineLearningEntities'),
          dispatch('fetchPatternAnyEntities'),
          dispatch('fetchPostprocessEntities'),
          dispatch('fetchPrebuiltEntities')
        ])

        const mlEntities = state.machineLearningEntities.value
          .map(entity => ({ ...entity, type: ENTITY_TYPE.machineLearning }))

        const regexpEntities = state.regexpEntities.value
          .map(entity => ({ ...entity, type: ENTITY_TYPE.regexp }))

        const prebuildEntities = state.prebuiltEntities.value
          .map(entity => ({ ...entity, type: ENTITY_TYPE.prebuilt }))

        const patternAnyEntities = state.patternAnyEntities.value
          .map(entity => ({ ...entity, type: ENTITY_TYPE.patternAny }))

        const listEntities = state.listEntities.value
          .map(entity => ({ ...entity, type: ENTITY_TYPE.list }))

        const allEntities = [
          ...mlEntities,
          ...regexpEntities,
          ...prebuildEntities,
          ...patternAnyEntities,
          ...listEntities
        ]
        commit('SET_ALL_ENTITIES', { value: allEntities })

        return allEntities
      } catch (error) {
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: 'store.entities.fetchAllEntities.errorNotification.title', error },
          { root: true }
        )
        commit('SET_ALL_ENTITIES', {})
      }
    },

    async deleteEntity ({ rootState, rootGetters, dispatch }, { item }) {
      try {
        await nluApi.deleteEntity({
          kbId: rootState.knowledgebase.current,
          ...item
        })
        await Promise.all([
          dispatch('fetchAllEntities'),
          dispatch('knowledgebase/fetchCategories', null, { root: true })
        ])
      } catch (error) {
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: 'store.entities.deleteEntity.errorNotification.title', error },
          { root: true }
        )
      }
    },

    async fetchListEntities ({ rootState, rootGetters, commit, dispatch }) {
      try {
        commit('SET_LIST_ENTITIES', { loading: true })
        const response = await nluApi.listAllEntities({
          kbId: rootState.knowledgebase.current,
          type: 'list'
        })
        commit('SET_LIST_ENTITIES', { value: response })

        return response
      } catch (error) {
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: 'store.entities.fetchListEntities.errorNotification.title', error },
          { root: true }
        )
        commit('SET_LIST_ENTITIES', {
          loading: false,
          value: []
        })
      }
    },

    async fetchMachineLearningEntities ({ rootState, commit, dispatch, rootGetters }) {
      try {
        commit('SET_ML_ENTITIES', { loading: true })
        const data = await nluApi.listAllEntities({
          kbId: rootState.knowledgebase.current,
          type: 'simple'
        })
        commit('SET_ML_ENTITIES', { value: data })

        return data
      } catch (error) {
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: 'store.entities.fetchMachineLearningEntities.errorNotification.title', error },
          { root: true }
        )
        commit('SET_ML_ENTITIES', {})
      }
    },

    async fetchPrebuiltEntities ({ commit, dispatch, rootGetters, rootState }) {
      try {
        commit('SET_PREBUILT_ENTITIES', { loading: true })
        const value = await nluApi.listAllEntities({
          kbId: rootState.knowledgebase.current,
          type: 'prebuilt'
        })
        commit('SET_PREBUILT_ENTITIES', { value })

        return value
      } catch (error) {
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: 'store.entities.fetchPrebuiltEntities.errorNotification.title', error },
          { root: true }
        )
        commit('SET_PREBUILT_ENTITIES', {})
      }
    },

    async createPrebuiltEntity ({ commit, dispatch, rootGetters, rootState }, { name, roles }) {
      try {
        const entity = await nluApi.createEntity({
          kbId: rootState.knowledgebase.current,
          type: 'prebuilt',
          roles: roles.map(r => r.replace(/`/g, '')),
          name
        })
        dispatch('knowledgebase/_updateModelModifiedDate', null, { root: true })
        await Promise.all([
          dispatch('fetchAllEntities'),
          dispatch('knowledgebase/fetchCategories', null, { root: true }),
          dispatch('knowledgebase/listAllModels', null, { root: true })
        ])

        return entity
      } catch (error) {
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: 'store.entities.createPrebuiltEntity.errorNotification.title', error },
          { root: true }
        )
        commit('SET_PREBUILT_ENTITIES', {})
      }
    },

    async updatePrebuiltEntity ({ commit, rootGetters, rootState, dispatch }, { name, roles, id }) {
      try {
        dispatch('knowledgebase/_updateModelModifiedDate', null, { root: true })
        await nluApi.updateEntity({
          kbId: rootState.knowledgebase.current,
          type: 'prebuilt',
          roles: roles.map(r => r.replace(/`/g, '')),
          id,
          name
        })
        await Promise.all([
          dispatch('fetchAllEntities'),
          dispatch('knowledgebase/fetchCategories', null, { root: true }),
          dispatch('knowledgebase/listAllModels', null, { root: true })
        ])
      } catch (error) {
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: 'store.entities.updatePrebuiltEntity.errorNotification.title', error },
          { root: true }
        )
        commit('SET_PREBUILT_ENTITIES', {})
      }
    },

    async deletePrebuilt ({ state, commit, getters, dispatch }, id) {
      // todo
    },

    async fetchAllPrebuiltEntities ({ commit, dispatch, rootGetters }) {
      try {
        const { culture: language } = rootGetters['knowledgebase/getCurrent']
        commit('SET_ALL_PREBUILT_ENTITIES', { loading: true })
        let data = await nluApi.listAllPrebuiltEntities({ language })
        data = _.map(data, 'name')
        commit('SET_ALL_PREBUILT_ENTITIES', { value: data })

        return data
      } catch (error) {
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: 'store.entities.fetchAllPrebuiltEntities.errorNotification.title', error },
          { root: true }
        )
        commit('SET_ALL_PREBUILT_ENTITIES', {})
      }
    },

    async fetchRegexpEntities ({ rootState, commit, rootGetters, dispatch }) {
      try {
        commit('SET_REGEXP_ENTITIES', { loading: true })
        const data = await nluApi.listAllEntities({
          kbId: rootState.knowledgebase.current,
          type: 'regex'
        })
        commit('SET_REGEXP_ENTITIES', { value: data })

        return data
      } catch (error) {
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: 'store.entities.fetchRegexpEntities.errorNotification.title', error },
          { root: true }
        )
      }
      commit('SET_REGEXP_ENTITIES', {})
    },

    async fetchPatternAnyEntities ({ rootState, commit, rootGetters, dispatch }) {
      try {
        commit('SET_PATTERN_ANY_ENTITIES', { loading: true })
        const data = await nluApi.listAllEntities({
          kbId: rootState.knowledgebase.current,
          type: ENTITY_TYPE.patternAny
        })
        commit('SET_PATTERN_ANY_ENTITIES', { value: data })

        return data
      } catch (error) {
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: 'store.entities.fetchPatternAnyEntities.errorNotification.title', error },
          { root: true }
        )
        commit('SET_PATTERN_ANY_ENTITIES', {})
      }
    },

    async fetchPostprocessEntities ({ rootState, commit, rootGetters, dispatch }) {
      try {
        commit('SET_POSTPROCESS_ENTITIES', { loading: true })
        const response = await nluApi.listAllEntities({
          kbId: rootState.knowledgebase.current,
          type: 'postprocess'
        })
        commit('SET_POSTPROCESS_ENTITIES', { value: response })

        return response
      } catch (error) {
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: 'store.entities.fetchPostprocessEntities.errorNotification.title', error },
          { root: true }
        )
        commit('SET_POSTPROCESS_ENTITIES', [])
      }
    }
  },

  mutations: {
    SET_LIST_ENTITIES (state, { loading = false, value = [] }) {
      _.set(state, 'listEntities', { loading, value })
    },

    SET_ML_ENTITIES (state, { loading = false, value = [] }) {
      _.set(state, 'machineLearningEntities', { loading, value })
    },

    SET_PREBUILT_ENTITIES (state, { loading = false, value = [] }) {
      _.set(state, 'prebuiltEntities', { loading, value })
    },

    SET_ALL_PREBUILT_ENTITIES (state, { loading = false, value = [] }) {
      _.set(state, 'allPrebuiltEntities', { loading, value })
    },

    SET_REGEXP_ENTITIES (state, { loading = false, value = [] }) {
      _.set(state, 'regexpEntities', { loading, value })
    },

    SET_PATTERN_ANY_ENTITIES (state, { loading = false, value = [] }) {
      _.set(state, 'patternAnyEntities', { loading, value })
    },

    SET_POSTPROCESS_ENTITIES (state, { loading = false, value = [] }) {
      _.set(state, 'postprocessEnities', { loading, value })
    },

    SET_ALL_ENTITIES (state, { loading = false, value = [] }) {
      _.set(state, 'allEntities', { loading, value })
    }
  }
}
